import { store } from "../../../store";
import { helpers } from "../../../packages/helpers";
import { auth } from "../actions";
import type { ControlState } from "../../control";

const { asyncEmpty } = helpers.utilities;

type Callback = (() => void) | (() => Promise<void>);

export class Requester {
  private queue: Array<Callback> = [];

  private isRefreshing = false;

  private intervalId: NodeJS.Timeout | null = null;

  private authApi: ControlState["api"]["authApi"] = asyncEmpty;

  private access = {
    token: "",
    expire: NaN,
  };

  private setAccess(token: string, expire: number) {
    this.access.token = token;
    this.access.expire = expire * 1000;
  }

  private async requestCallbackFromQueue() {
    const callback = this.queue.shift();

    await callback?.();

    const isEmptyQueue = !this.queue.length;

    if (isEmptyQueue && this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private async runQueue() {
    const intervalCallback = async () => {
      if (this.isRefreshing || !this.access.token) {
        return;
      }

      while (this.queue.length > 0) {
        await this.requestCallbackFromQueue();
      }
    };

    this.intervalId = setInterval(intervalCallback, 200);
  }

  setAuthCallback(authApi: ControlState["api"]["authApi"]) {
    this.authApi = authApi;
  }

  turnOnRefreshing() {
    this.isRefreshing = true;
  }

  turnOffRefreshing(token = "", expire = NaN) {
    this.isRefreshing = false;
    this.setAccess(token, expire);
  }

  async addCallbackToQueue(callback: Callback) {
    const { token, expire } = this.access;
    const diff = expire - new Date().getTime();
    const isRuntime = !isNaN(expire);
    const isNotRefreshing = !this.isRefreshing;
    const isRottenToken = diff < 10000;
    const { authApi } = this;

    if ((isRuntime && isNotRefreshing && isRottenToken) || !token) {
      await store.dispatch(auth({ authApi }));
    }

    if (isNotRefreshing && token) {
      callback();

      return;
    }

    this.queue.push(callback);

    if (!this.intervalId) {
      this.runQueue();
    }
  }
}
